// SPDX-License-Identifier: Elastic-2.0
/**
 * ToastProvider — owns the visible toast stack + queue.
 *
 * Behavior (per T-011 spec):
 *  - Max 3 toasts visible simultaneously.
 *  - Additional `show()` calls queue and slot in as visible ones dismiss.
 *  - Default auto-dismiss = 4000ms; `durationMs: 0` disables auto-dismiss.
 *  - `dismiss(id)` triggers the exit animation (~200ms) then removes the
 *    toast from state.
 *  - The stack container carries `aria-live="polite"` so dynamically
 *    appended toasts are announced; individual toasts choose
 *    `role="alert"` (warning/error) vs `role="status"` (info/success).
 *
 * Timer ownership: each visible toast's dismiss timer + exit timer is
 * tracked in a single ref-held Map so React renders don't reset them.
 * Cleanup runs on unmount.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Toast } from "./Toast";
import type { ToastInstance, ToastOptions } from "./Toast";
import { ToastContext } from "./useToast";
import type { ToastApi } from "./useToast";
import styles from "./Toast.module.css";

/** Maximum number of toasts visible at once. Spec: 3. */
const MAX_VISIBLE = 3;
/** Default auto-dismiss timeout. Spec: 4000ms. */
const DEFAULT_DURATION_MS = 4000;
/** Duration of the exit animation (must match `.exiting` CSS). */
const EXIT_DURATION_MS = 200;

interface TrackedToast extends ToastInstance {
  /** True while the exit animation is playing. */
  exiting: boolean;
}

export interface ToastProviderProps {
  children: ReactNode;
  /**
   * Override the default auto-dismiss duration. Tests use a short value
   * to avoid waiting; production should rely on the 4000ms default.
   */
  defaultDurationMs?: number;
}

let nextId = 0;
function makeId(): string {
  nextId += 1;
  return `t-${nextId.toString(36)}-${Date.now().toString(36)}`;
}

export function ToastProvider({
  children,
  defaultDurationMs = DEFAULT_DURATION_MS,
}: ToastProviderProps): ReactElement {
  const { t } = useTranslation();
  const [visible, setVisible] = useState<TrackedToast[]>([]);
  const queueRef = useRef<ToastInstance[]>([]);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  /** Forward-declared promote callback hole — assigned below to avoid TDZ. */
  const promoteRef = useRef<() => void>(() => {});

  /**
   * Begin removing a toast: play the exit animation, then drop it from
   * state and promote a queued toast (if any) into the freed slot.
   */
  const dismiss = useCallback((id: string): void => {
    // Clear any pending auto-dismiss timer for this id.
    const pending = timersRef.current.get(id);
    if (pending !== undefined) {
      clearTimeout(pending);
      timersRef.current.delete(id);
    }
    // Mark as exiting so the CSS animation plays.
    setVisible((prev) => prev.map((tw) => (tw.id === id ? { ...tw, exiting: true } : tw)));
    // After animation, actually remove + promote from queue.
    const exitTimer = setTimeout(() => {
      timersRef.current.delete(`exit-${id}`);
      setVisible((prev) => prev.filter((tw) => tw.id !== id));
      promoteRef.current();
    }, EXIT_DURATION_MS);
    timersRef.current.set(`exit-${id}`, exitTimer);
  }, []);

  /**
   * Move toasts from the queue into the visible stack until either
   * the queue empties or we hit MAX_VISIBLE.
   */
  const promote = useCallback((): void => {
    setVisible((prev) => {
      const liveCount = prev.filter((tw) => !tw.exiting).length;
      const slots = MAX_VISIBLE - liveCount;
      if (slots <= 0 || queueRef.current.length === 0) {
        return prev;
      }
      const taken = queueRef.current.splice(0, slots);
      const promoted: TrackedToast[] = taken.map((toast) => ({ ...toast, exiting: false }));
      // Schedule auto-dismiss for each promoted toast (unless durationMs === 0).
      for (const toast of promoted) {
        const ms = toast.durationMs ?? defaultDurationMs;
        if (ms > 0) {
          const timer = setTimeout(() => {
            timersRef.current.delete(toast.id);
            dismiss(toast.id);
          }, ms);
          timersRef.current.set(toast.id, timer);
        }
      }
      return [...prev, ...promoted];
    });
  }, [defaultDurationMs, dismiss]);

  // Keep the ref in sync so `dismiss` can call the latest promote
  // without `promote` being in its dep list (avoids re-binding both
  // callbacks every render).
  useEffect(() => {
    promoteRef.current = promote;
  }, [promote]);

  const show = useCallback(
    (opts: ToastOptions): string => {
      const id = makeId();
      const instance: ToastInstance = { ...opts, id };
      queueRef.current.push(instance);
      promote();
      return id;
    },
    [promote],
  );

  // Cleanup all timers on unmount so tests / route changes don't leak.
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((tmr) => clearTimeout(tmr));
      timers.clear();
    };
  }, []);

  const api = useMemo<ToastApi>(() => ({ show, dismiss }), [show, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        // role="status" gives the live region a semantic identity so
        // assistive tech treats the aria-label as the region's name.
        // axe-core's `aria-prohibited-attr` rule otherwise flags
        // aria-label on a plain <div> with no role as unusable.
        role="status"
        className={styles.region}
        aria-live="polite"
        aria-label={t("feedback.toast_region_label")}
        data-testid="toast-region"
      >
        {visible.map((tw) => (
          <Toast key={tw.id} toast={tw} exiting={tw.exiting} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}
